/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useState } from 'react';
import IconButton from '@/components/Redactie/IconButton/IconButton';
import Modal from '@/components/Redactie/Modal/Modal';
import EditButtonBar from '@/components/Redactie/IconButton/EditButtonBar';
import Chip from '@/components/Redactie/Chip/Chip';
import {
    mdiArrowDownCircle,
    mdiArrowUpCircle,
    mdiDelete,
    mdiPlaylistEdit,
} from '@mdi/js';

function ListEditor<T>({
    list,
    onSaved,
    createNewItem,
    validateItem,
    validateList,
    listDescription,
    listDisplayName,
    listItemDisplayName,
    minItems = 2,
    maxItems = 5,
    listSummaryItemData,
    listItemTemplate,
}: {
    list: T[];
    onSaved: (list: T[]) => void;
    createNewItem: () => T;
    validateItem: (
        item: T,
    ) => {
        isValid: boolean;
        validationText: string;
    };
    validateList: (
        list: T[],
    ) => {
        isValid: boolean;
        validationText: string;
    };
    listDescription?: string;
    listDisplayName: string;
    listItemDisplayName: string;
    minItems?: number;
    maxItems?: number;
    listSummaryItemData?: (
        item: T,
    ) => {
        displayName: string;
        icon: string | undefined;
    };
    listItemTemplate: (
        index: number,
        item: T,
        setItem: (item: T) => void,
    ) => JSX.Element;
}) {
    const [isEditorOpened, setIsEditorOpened] = useState(false);
    const [editedList, setEditedList] = useState<T[]>(list);

    useEffect(() => {
        setEditedList(list);
    }, [list]);

    const isEdited = JSON.stringify(list) !== JSON.stringify(editedList);
    const listValidation = validateList(editedList);
    const isSaveable =
        listValidation.isValid &&
        editedList.every((item) => validateItem(item).isValid);

    return (
        <>
            <div
                className="grid gap-2 cursor-pointer"
                onClick={() => setIsEditorOpened(true)}
            >
                {!!listSummaryItemData && (
                    <ol className="flex flex-wrap gap-1 p-1 pb-0 items-center justify-center">
                        {list.map((item, index) => (
                            <Chip
                                key={index}
                                icon={listSummaryItemData(item).icon}
                            >
                                {listSummaryItemData(item).displayName}
                            </Chip>
                        ))}
                    </ol>
                )}
                <IconButton
                    icon={mdiPlaylistEdit}
                    label="Items toevoegen, bewerken of verwijderen"
                    badgeValue={list.length}
                    onClick={() => setIsEditorOpened(true)}
                    className="mx-auto"
                >
                    {listDisplayName} bewerken
                </IconButton>
            </div>
            <Modal
                isOpen={isEditorOpened}
                onClose={() => setIsEditorOpened(false)}
                title={`${listDisplayName} bewerken`}
                footer={
                    <div className="p-4">
                        {!listValidation.isValid &&
                            !!listValidation.validationText && (
                                <p className="mb-4 text-rose-600 text-sm font-bold">
                                    {listValidation.validationText}
                                </p>
                            )}
                        <EditButtonBar
                            saveButton={{
                                show: true,
                                enabled: isEdited && isSaveable,
                                onClick: () => {
                                    onSaved(editedList);
                                    setIsEditorOpened(false);
                                },
                            }}
                            addButton={{
                                show: true,
                                enabled: editedList.length < maxItems,
                                onClick: () => {
                                    setEditedList([
                                        ...editedList,
                                        createNewItem(),
                                    ]);
                                },
                                labelText: `${listItemDisplayName} toevoegen`,
                            }}
                            cancelButton={{
                                show: true,
                                enabled: true,
                                onClick: () => {
                                    setEditedList(list);
                                    setIsEditorOpened(false);
                                },
                            }}
                        />
                    </div>
                }
            >
                {!!listDescription && (
                    <p className="text-xl font-bold">{listDescription}</p>
                )}
                <ol className="grid gap-4">
                    {editedList.map((item, index) => (
                        <ListEditorItem
                            key={index}
                            validationText={
                                validateItem(item).isValid
                                    ? undefined
                                    : validateItem(item).validationText
                            }
                            isFirstItem={index === 0}
                            isLastItem={index === editedList.length - 1}
                            canBeDeleted={editedList.length > minItems}
                            onMoved={(by) => {
                                const newList = [...editedList];
                                const newIndex = index + by;
                                if (
                                    newIndex < 0 ||
                                    newIndex >= editedList.length
                                ) {
                                    return;
                                }
                                const temp = newList[newIndex];
                                newList[newIndex] = newList[index];
                                newList[index] = temp;
                                setEditedList(newList);
                            }}
                            onDeleted={() => {
                                const newList = [...editedList];
                                newList.splice(index, 1);
                                setEditedList(newList);
                            }}
                        >
                            {listItemTemplate(index, item, (newItem) => {
                                const newList = [...editedList];
                                newList[index] = newItem;
                                setEditedList(newList);
                            })}
                        </ListEditorItem>
                    ))}
                </ol>
            </Modal>
        </>
    );
}

function ListEditorItem({
    children,
    validationText,
    isFirstItem,
    isLastItem,
    canBeDeleted,
    onMoved,
    onDeleted,
}: {
    children: JSX.Element;
    validationText?: string;
    isLastItem: boolean;
    isFirstItem: boolean;
    canBeDeleted: boolean;
    onMoved: (by: number) => void;
    onDeleted: () => void;
}) {
    return (
        <li className="flex flex-col gap-2 w-full">
            <div className="flex w-full">
                <div className="grow">{children}</div>
                <div className="shrink-0 flex flex-col justify-center ps-4 gap-2">
                    <IconButton
                        isMini
                        icon={mdiArrowUpCircle}
                        label="Omhoog verplaatsen"
                        onClick={() => onMoved(-1)}
                        enabled={!isFirstItem}
                    />
                    <IconButton
                        isMini
                        icon={mdiDelete}
                        label="Verwijderen"
                        onClick={onDeleted}
                        enabled={canBeDeleted}
                    />
                    <IconButton
                        isMini
                        icon={mdiArrowDownCircle}
                        label="Omlaag verplaatsen"
                        onClick={() => onMoved(1)}
                        enabled={!isLastItem}
                    />
                </div>
            </div>
            {!!validationText && (
                <p className="text-rose-600 text-sm font-bold">
                    {validationText}
                </p>
            )}
        </li>
    );
}

export default ListEditor;
